// İSG uzmanı portalı — Taşeron/Yüklenici (Contractors) erişim sondası (route guard UX).
//
// Asıl yetki backend'dedir: her Taşeron ucu `[ModuleAction("contractors", …)]` taşır;
// grant'ı olmayan principal → 403 (api-contract §13). Bu sonda yalnız UX görünürlüğüdür —
// hafif bir liste probu (`GET /contractors?page=1&pageSize=1`) çeker:
//   - 2xx  → erişim var (granted) → Taşeron kabuğu + menü görünür
//   - 403  → yetki yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (404/503/ağ) → servis/veri kullanılamıyor (unavailable) → yeniden dene
//
// Ayarlar (§10) / Ziyaretçi (§11) sondalarıyla aynı desendir; iş modülü olarak AYRIDIR.
// Probun `filteredTotal`'ı boş-durum ("henüz taşeron yok") kararında da kullanılabilir.

import { ApiError, contractorsApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { contractorsKeys } from './contractorsKeys';

export type ContractorsAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface ContractorsAccess {
  status: ContractorsAccessStatus;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * Taşeron erişimini backend sondasıyla belirler. Oturum açık (RequireAuth altı)
 * varsayılır. 4xx'te retry yok (anında sonuç); 403 (yetki yok) ≠ 404/503 (servis/veri)
 * ayrımı korunur.
 */
export function useContractorsAccess(): ContractorsAccess {
  const query = useQuery({
    queryKey: contractorsKeys.access(),
    queryFn: () => contractorsApi.listContractors({ page: 1, pageSize: 1 }),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: ContractorsAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, refetch: () => void query.refetch() };
}
